import React, { useEffect } from "react";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { ClockCheckOutline } from "mdi-material-ui";
import * as z from "zod";
import { TextField } from "~/components/Form";
import { requiredText } from "~/domain/common";
import { useForm } from "~/lib/form";
import {
  useFormatPlaybackTimestamp,
  useLoadedPlaybackSource,
  usePlaybackSettings,
  usePlaybackSource,
  usePlayerActions,
} from "~/player";
import { parseBigInt } from "~/utils/math";
import { parseDuration } from "../sidebars";

interface Constraint {
  value: bigint;
  formatted: string;
  exclusive: boolean;
}

interface SelectionFieldProps {
  name: string;
  value: bigint;
  min: bigint;
  exclusiveMin?: boolean;
  max: bigint;
  exclusiveMax?: boolean;
  onSubmit: (newValue: bigint) => void;
}

function refineTimestamp(
  value: bigint,
  min: Constraint,
  max: Constraint,
  ctx: z.RefinementCtx,
): boolean {
  if (min.exclusive && !(value > min.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Must be greater than ${min.formatted}`,
      fatal: true,
    });

    return false;
  }

  if (!min.exclusive && !(value >= min.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Cannot be less than ${min.formatted}`,
      fatal: true,
    });

    return false;
  }

  if (max.exclusive && !(value < max.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Must be less than ${max.formatted}`,
      fatal: true,
    });

    return false;
  }

  if (!max.exclusive && !(value <= max.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Cannot be greater than ${max.formatted}`,
      fatal: true,
    });

    return false;
  }

  return true;
}

export function durationTimestampSchema(
  referenceTime: bigint,
  min: Constraint,
  max: Constraint,
) {
  return requiredText.transform((value, ctx) => {
    const parsedDuration = parseDuration(value);
    if (parsedDuration === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid duration",
        fatal: true,
      });

      return z.NEVER;
    }

    const timestamp = parsedDuration + referenceTime;

    if (refineTimestamp(timestamp, min, max, ctx)) {
      return timestamp;
    } else {
      return z.NEVER;
    }
  });
}

export function rawTimestampSchema(min: Constraint, max: Constraint) {
  return requiredText.transform((value, ctx) => {
    const timestamp = parseBigInt(value);

    if (timestamp == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid timestamp",
        fatal: true,
      });

      return z.NEVER;
    }

    if (refineTimestamp(timestamp, min, max, ctx)) {
      return timestamp;
    } else {
      return z.NEVER;
    }
  });
}

export function TimestampSelectionForm({
  mode,
}: {
  mode: "instant" | "range";
}) {
  const { displayFormat } = usePlaybackSettings();

  const playbackSource = usePlaybackSource();

  const playerActions = usePlayerActions();

  if (playbackSource.isLoading) {
    return null;
  }

  const { bounds, timestamp, range } = playbackSource;

  function handleInstantSubmit(instant: bigint): void {
    playerActions.seek(instant);
  }

  function handleRangeStartSubmit(rangeStart: bigint): void {
    playerActions.setRange({
      startTime: rangeStart,
      endTime: range.endTime,
    });
    playerActions.seek(rangeStart);
  }

  function handleRangeEndSubmit(rangeEnd: bigint): void {
    playerActions.setRange({
      startTime: range.startTime,
      endTime: rangeEnd,
    });
    playerActions.seek(rangeEnd);
  }

  switch (true) {
    case displayFormat === "elapsed" && mode === "instant": {
      return (
        <DurationTimestampForm
          name="instant"
          value={timestamp}
          min={bounds.startTime}
          max={bounds.endTime}
          onSubmit={handleInstantSubmit}
        />
      );
    }
    case displayFormat === "elapsed" && mode === "range": {
      return (
        <Stack spacing={1.5}>
          <DurationTimestampForm
            name="rangeStart"
            value={range.startTime}
            min={bounds.startTime}
            max={range.endTime}
            exclusiveMax
            onSubmit={handleRangeStartSubmit}
          />
          <DurationTimestampForm
            name="rangeEnd"
            value={range.endTime}
            min={range.startTime}
            exclusiveMin
            max={bounds.endTime}
            onSubmit={handleRangeEndSubmit}
          />
        </Stack>
      );
    }
    case displayFormat === "original" && mode === "instant": {
      return (
        <RawTimestampForm
          name="instant"
          value={timestamp}
          min={bounds.startTime}
          max={bounds.endTime}
          onSubmit={handleInstantSubmit}
        />
      );
    }
    case displayFormat === "original" && mode === "range": {
      return (
        <Stack spacing={1.5}>
          <RawTimestampForm
            name="rangeStart"
            value={range.startTime}
            min={bounds.startTime}
            max={range.endTime}
            exclusiveMax
            onSubmit={handleRangeStartSubmit}
          />
          <RawTimestampForm
            name="rangeEnd"
            value={range.endTime}
            min={range.startTime}
            exclusiveMin
            max={bounds.endTime}
            onSubmit={handleRangeEndSubmit}
          />
        </Stack>
      );
    }
    default: {
      throw new Error("Unexpected mode and/or display format");
    }
  }
}

function DurationTimestampForm({
  name,
  value,
  min,
  exclusiveMin = false,
  max,
  exclusiveMax = false,
  onSubmit,
}: SelectionFieldProps) {
  const { bounds } = useLoadedPlaybackSource();

  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  const form = useForm({
    schema: z.object({
      [name]: durationTimestampSchema(
        bounds.startTime,
        {
          value: min,
          formatted: formatPlaybackTimestamp(min, "elapsed"),
          exclusive: exclusiveMin,
        },
        {
          value: max,
          formatted: formatPlaybackTimestamp(max, "elapsed"),
          exclusive: exclusiveMax,
        },
      ),
    }),
    values: {
      [name]: formatPlaybackTimestamp(value, "elapsed"),
    },
    onSubmit(values) {
      onSubmit(values[name]);
    },
  });

  useResetSubmittedForm(form);

  const fieldValue = form.watch(name);

  const isDirty = form.formState.dirtyFields[name] ?? false;

  return (
    <form noValidate onSubmit={form.handleSubmit}>
      <TextField
        control={form.control}
        name={name}
        size="small"
        endAdornment={
          <TimestampEndAdornment
            value={fieldValue}
            action={
              <Tooltip title="Update">
                <span>
                  <IconButton
                    type="submit"
                    disabled={!isDirty}
                    edge="end"
                    size="small"
                  >
                    <ClockCheckOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            }
          />
        }
        helperText={
          isDirty ? (
            <span>
              Press{" "}
              <Typography
                variant="inherit"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                Update (
                <ClockCheckOutline
                  sx={{ fontSize: "1.25em", verticalAlign: "text-bottom" }}
                />
                )
              </Typography>{" "}
              to set time
            </span>
          ) : undefined
        }
      />
    </form>
  );
}

function RawTimestampForm({
  name,
  value,
  min,
  exclusiveMin = false,
  max,
  exclusiveMax = false,
  onSubmit,
}: SelectionFieldProps) {
  const form = useForm({
    schema: z.object({
      [name]: rawTimestampSchema(
        { value: min, formatted: String(min), exclusive: exclusiveMin },
        { value: max, formatted: String(max), exclusive: exclusiveMax },
      ),
    }),
    values: {
      [name]: String(value),
    },
    onSubmit(values) {
      onSubmit(values[name]);
    },
  });

  useResetSubmittedForm(form);

  const fieldValue = form.watch(name);

  const isDirty = form.formState.dirtyFields[name] ?? false;

  return (
    <form noValidate onSubmit={form.handleSubmit}>
      <TextField
        control={form.control}
        name={name}
        size="small"
        endAdornment={
          <TimestampEndAdornment
            value={fieldValue}
            action={
              <Tooltip title="Update">
                <span>
                  <IconButton
                    type="submit"
                    disabled={!isDirty}
                    edge="end"
                    size="small"
                  >
                    <ClockCheckOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            }
          />
        }
        helperText={
          isDirty ? (
            <span>
              Press{" "}
              <Typography
                variant="inherit"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                Update (
                <ClockCheckOutline
                  sx={{ fontSize: "1.25em", verticalAlign: "text-bottom" }}
                />
                )
              </Typography>{" "}
              to set time
            </span>
          ) : undefined
        }
      />
    </form>
  );
}

export function TimestampEndAdornment({
  value,
  action,
}: {
  value: string | null;
  action: React.ReactNode;
}) {
  const { displayFormat } = usePlaybackSettings();

  const { bounds } = useLoadedPlaybackSource();

  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  let prefix = null;
  if (displayFormat === "original") {
    const parsedValue = parseBigInt(value);

    if (
      parsedValue != null &&
      bounds.startTime <= parsedValue &&
      parsedValue <= bounds.endTime
    ) {
      prefix = (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          ({formatPlaybackTimestamp(parsedValue, "elapsed")})
        </Typography>
      );
    }
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      {prefix}
      {action}
    </Stack>
  );
}

function useResetSubmittedForm(form: {
  reset: () => void;
  formState: { isSubmitSuccessful: boolean };
}): void {
  const {
    reset,
    formState: { isSubmitSuccessful },
  } = form;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);
}
